import { Md5 } from 'ts-md5';
import axios from 'axios';
import {
  generateFirebaseUploadUrl,
  isMediaImage
} from '../../../redux/slices/lomavisCreator/utils';
import {
  compressImage,
  determineImageDimensionsFromURL
} from '../../lomavisCreator/utils/helpers';
import { UPLOAD_TYPE_POST_MEDIA } from '../../../config/constants';
import axiosClient from '../../../utils/axios';

export const replaceDomainInUrl = (
  originalUrl: string,
  oldDomain: string,
  newDomain: string
): string => {
  try {
    // Convert www.oldDomain.com to oldDomain.com for a broader match
    const normalizedOldDomain = oldDomain.replace(/^www\./, '');
    const normalizedNewDomain = newDomain.replace(/^www\./, '');


    if (originalUrl.indexOf("images/redirect-to-external-image/?url") === -1) {
      // Use regex to replace domain while keeping the URL structure intact
      return originalUrl.replace(
          new RegExp(
              `(https?:\\/\\/cdn\\.brandfetch\\.io\\/)${normalizedOldDomain}`
          ),
          `$1${normalizedNewDomain}`
      );
    }else{
      return originalUrl.replace(oldDomain, newDomain)
    }

  } catch (error) {
    console.error('Error replacing domain in URL:', error);
    return originalUrl; // Return original URL in case of error
  }
};

export const isSquareImage = async (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => resolve(img.width === img.height);
    img.onerror = () => resolve(false); // Default to non-square if there's an error
  });
};

export const generateImageHash = async (imageUrl: string): Promise<string> => {
  const response = await fetch(imageUrl);

  // Check if the image URL is valid
  if (!response.ok) {
    console.error('Failed to generate hash:');
    // You can return a default value or handle the error as needed
    return null;
  }

  const imageBlob = await response.blob(); // Get the image as a blob

  // Convert the Blob to base64 string
  const base64Image = await blobToBase64(imageBlob);

  // Generate the MD5 hash of the base64 string
  const hash = Md5.hashStr(base64Image);

  return hash;
};

// Helper function to convert a Blob to a Base64 string
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const replaceUrl = (newUrl: string): string => {
  const currentOrigin = window.location.origin; // Get the current origin

  if (newUrl.indexOf("images/redirect-to-external-image") !== -1) {
    return newUrl;

  }
  return `${currentOrigin}/images/redirect-to-external-image/?image_url=${newUrl}`;
};

export const handleImageUploadWithUrl = async (
  imageUrl: string,
  location_uuid: string,
  cloud_uuid: string | null,
  onFailure: (error: any) => void
) => {
  try {
    // Fetch the image and convert it to a Blob
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    // Convert to PNG using an offscreen canvas
    const pngBlob = await convertToPNG(blob);
    const fileType = 'image/png';

    // Create a File object from the PNG blob
    const file = new File([pngBlob], 'uploaded-image.png', { type: fileType });

    // Compress the image
    const compressedFile = await compressImage(file);

    // Generate a preview URL
    let previewImage = URL.createObjectURL(compressedFile);
    const fileSizeInBytes = compressedFile.size;

    // Get image dimensions
    let dimensions = await determineImageDimensionsFromURL(previewImage);

    // Generate upload URL
    let uploadPreset = await generateFirebaseUploadUrl(
      {
        content_type: fileType,
        filesize_in_bytes: fileSizeInBytes,
        filename: compressedFile.name,
        location_uuid: location_uuid,
        cloud_uuid: cloud_uuid,
        width: dimensions?.width,
        height: dimensions?.height,
        upload_type: UPLOAD_TYPE_POST_MEDIA
      },
      {},
      () => console.log('error here')
    );

    const url = uploadPreset?.url;
    const headers = uploadPreset?.headers;

    // Upload the file
    const config = {
      headers: headers ? { ...headers, 'Content-Type': fileType } : {}
    };

    const uploadResponse = await axios.put(url, compressedFile, config);

    console.log('Upload response:', uploadResponse);

    // Confirm the upload
    const confirmResponse = await axiosClient.put(uploadPreset?.confirm_url, {
      success: true
    });

    if (confirmResponse.data) {
      return uploadPreset?.uuid;
    }
  } catch (error) {
    console.error('Upload failed:', error);
    onFailure(error);
  }
};
export const convertToPNG = async (blob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((pngBlob) => {
          if (pngBlob) resolve(pngBlob);
          else reject(new Error('Failed to convert to PNG'));
        }, 'image/png');
      } else {
        reject(new Error('Canvas context is null'));
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
};
